/**
 * Wie eine Vorlesung in blätterbare Abschnitte zerfällt. (05.09.2026)
 *
 * ## Warum diese Datei entstanden ist
 *
 * Sie stand als `splitNarrationSections` in `frontend/app.ts` — und NUR dort.
 * Am 04.09. habe ich die Vorab-Vertonung des Essenz-Spiegels gebaut: nachts
 * den Text schon einmal sprechen lassen, damit Martin morgens nicht wartet.
 * Sie lief auch, zweimal, 155 s und 247 s.
 *
 * Genützt hat sie nichts. Der Player fordert nicht den ganzen Spiegel an,
 * sondern **einen Abschnitt** (`app.ts` → `bodyFor`: `text: s.text`), und der
 * Zwischenspeicher hängt am Text. Vorgewärmt war der ganze Spiegel, geholt
 * wurde ein Viertel davon — der Schlüssel traf nie. Martin sah weiterhin
 * „Bereitet vor …" und wartete.
 *
 * Die Ursache ist nicht die Vertonung, sondern dass der Server nicht wissen
 * konnte, wie das Frontend zerlegt. Also steht die Zerlegung jetzt hier, wo
 * beide sie lesen.
 *
 * ⚠️ Wer hier etwas ändert, ändert damit auch, was vorgewärmt wird. Beides
 *    MUSS dieselbe Funktion benutzen, sonst kehrt genau dieser Fehler zurück:
 *    ein Zwischenspeicher, der gefüllt wird und nie trifft.
 */

/**
 * Zerlegt eine Markdown-Vorlesung in blätterbare Abschnitte an den
 * Überschriften (`#…` oder fette „**Titel**"-Zeilen). Vorspann vor der ersten
 * Überschrift wird zu „Überblick". Ohne Überschriften: EIN Abschnitt.
 */
export function splitNarrationSections(
  md: string,
): Array<{ title: string; text: string }> {
  const headingOf = (line: string): string | null => {
    const h = /^\s*#{1,6}\s+(.*\S)\s*$/.exec(line);
    if (h) return h[1] ?? null;
    const b = /^\s*\*\*(.+?)\*\*\s*:?\s*$/.exec(line);
    if (b) return b[1] ?? null;
    // 2026-06-30 (Martin): nummerierte Fett-Titel auch als Trenner
    // („1. **Dein Tag**: …") — Essenz/Mein Leben/HD/Projekte zerfallen so
    // zuverlaessig in Tracks/Kapitel (Essenz-Auto-Continue greift sichtbar).
    const nb = /^\s*\d+[.)]\s*\*\*(.+?)\*\*\s*:?/.exec(line);
    if (nb) return nb[1] ?? null;
    return null;
  };
  const groups: Array<{ title: string; lines: string[] }> = [];
  let cur: { title: string; lines: string[] } | null = null;
  for (const line of (md || "").split("\n")) {
    const title = headingOf(line);
    if (title) {
      cur = { title, lines: [line] };
      groups.push(cur);
    } else if (cur) {
      cur.lines.push(line);
    } else if (line.trim()) {
      cur = { title: "Überblick", lines: [line] };
      groups.push(cur);
    }
  }
  const roh = groups
    .map((g) => ({ title: g.title, text: g.lines.join("\n").trim() }))
    .filter((s) => s.text.length > 0);
  /*
   * ⚠️ 2026-09-02 — Martin, mit Foto des Essenz-Spiegels: *„Obwohl eine Voice
   * läuft, steht der Player."*
   *
   * Beim Nachmessen fiel ein zweiter Fehler auf, der die Seite seit jeher
   * kleinredet. An seinem echten Spiegel gemessen:
   *
   *     1/4  „Spiegel des Tages — 2. September 2026"     39 Zeichen  ~3 s
   *     2/4  Dein Tag                                  1109 Zeichen ~79 s
   *     3/4  Der Abgleich                              2421 Zeichen ~173 s
   *     4/4  Der Spiegel                               1070 Zeichen ~76 s
   *
   * Abschnitt 1 ist die ÜBERSCHRIFT des Ganzen — ein Datum, das als eigenes
   * Kapitel vorgelesen wird. Der Zähler sagt „1/4", und drei Sekunden später
   * ist es vorbei. Wer die Vorlesung startet, hört als Erstes ein Datum und
   * muss dann warten, bis der nächste Abschnitt synthetisiert ist.
   *
   * Eine Überschrift ohne eigenen Text ist kein Kapitel. Sie gehört zu dem,
   * was ihr folgt — also wird sie damit verschmolzen. Steht sie ganz am
   * Schluss (nichts folgt mehr), bleibt sie stehen: dann ist sie das Einzige,
   * was es gibt, und Weglassen wäre Datenverlust.
   */
  const out: Array<{ title: string; text: string }> = [];
  for (let i = 0; i < roh.length; i++) {
    const s2 = roh[i]!;
    const nurUeberschrift =
      s2.text.split("\n").filter((z) => z.trim()).length <= 1;
    if (nurUeberschrift && i < roh.length - 1) {
      const naechster = roh[i + 1]!;
      out.push({
        // Der Titel des FOLGENDEN gewinnt — er benennt den Inhalt.
        title: naechster.title,
        text: `${s2.text}\n\n${naechster.text}`,
      });
      i++; // den verschmolzenen überspringen
      continue;
    }
    out.push(s2);
  }
  return out.length ? out : [{ title: "", text: (md || "").trim() }];
}
